import { useNavigate, useParams } from 'react-router-dom';
import { AgentActionRequest, AgentActionResponse } from './types';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { createAgentAction, deleteAgentAction, getAgentAction, getAgentActions, updateAgentAction } from './api';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import * as urls from 'routes/config';

export function useGetAgentActions(
  page: number,
  options?: UseQueryOptions<{ data: AgentActionResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();
  return useQuery<{ data: AgentActionResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agentActions', workspaceId, agentId],
    queryFn: () => getAgentActions(workspaceId!, agentId!, page),
    enabled: Boolean(workspaceId && agentId),
    ...options
  });
}

export function useGetAgentAction(options?: UseQueryOptions<AgentActionResponse, Error>) {
  const { workspaceId, agentId, actionId } = useParams<{
    workspaceId: string;
    agentId: string;
    actionId: string;
  }>();

  return useQuery<AgentActionResponse>({
    queryKey: ['agentAction', workspaceId, agentId, actionId],
    queryFn: () => getAgentAction(workspaceId!, agentId!, actionId!),
    enabled: Boolean(workspaceId && agentId && actionId),
    ...options
  });
}

export function useCreateOrUpdateAgentAction() {
  const navigate = useNavigate();
  const { workspaceId, agentId, actionId } = useParams<{
    workspaceId: string;
    agentId: string;
    actionId: string;
  }>();

  return useMutation({
    mutationFn: (payload: AgentActionRequest) =>
      actionId
        ? updateAgentAction(workspaceId!, agentId!, actionId!, payload)
        : createAgentAction(workspaceId!, agentId!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['agentActions', workspaceId, agentId],
          refetchType: 'active'
        });
        queryClient.invalidateQueries({
          queryKey: ['agent', workspaceId],
          refetchType: 'active'
        });
        if (actionId) {
          queryClient.invalidateQueries({
            queryKey: ['agentAction', workspaceId, agentId, actionId],
            refetchType: 'active'
          });
        }
        localStorage.removeItem('tempData');
        navigate(`${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}/${urls.configure.action}`);
      }
    }
  });
}

export function useDeleteAgentAction() {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteAgentAction(workspaceId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agentActions'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId],
        refetchType: 'all'
      });
    }
  });
}

export function useUpdateActionStatus() {
  const { workspaceId, agentId } = useParams<{
    workspaceId: string;
    agentId: string;
  }>();
  return useMutation({
    mutationFn: (payload: any) => updateAgentAction(workspaceId!, agentId!, payload?.agent_action?.id!, payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['agentActions', workspaceId, agentId],
          refetchType: 'active'
        });
      }
    }
  });
}
